<template>
    <div v-if="!edit" class="form-inline-edit">
        <div class="form-inline-edit-form">
            <label class="label">{{ label }}:</label>
            <div class="form-inline-edit-content">
                <span class="form-inline-edit-display" v-html="display"></span>
                <a class="form-inline-edit-link" @click.prevent="edit = true">
                    <fa-icon icon="pencil" />
                </a>
            </div>
        </div>
    </div>
    <div v-else class="form-inline-edit is-editing">
        <form class="form-inline-edit-form" @submit.prevent="$emit('save')">
            <label class="label">{{ label }}:</label>

            <div class="form-inline-edit-content">
                <form-input v-model="editValue" :type="type" :formatter="formatter" :focus="true">
                </form-input>

                <form-button :type="'submit'" :form="form" class="is-primary"> Save </form-button>
            </div>
        </form>

        <p v-if="error" class="help has-text-danger" v-text="error"></p>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';

export default {
    emits: ['save', 'input'],
    
    props: {
        value: {
            default: '',
            required: true,
        },
        form: {
            type: Form,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        display: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            default: 'text',
        },
        error: {
            type: String,
            default: '',
        },
        formatter: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            edit: false,
            editValue: '',
        };
    },

    computed: {
        ...mapState(stores.Form, ['currentInlineEdit']),
    },

    watch: {
        /**
         * Resets the form.
         *
         * @return {void}
         */
        edit(edit) {
            if (edit) {
                this.$store.dispatch('setCurrentInlineEdit', this.$.uid);
                this.initEventHandlers();
            } else {
                this.form.reset();
                this.clearEventHandlers();
            }
        },

        /**
         * Watches the currentInlineEdit to disable this form.
         *
         * @param {Number} id
         * @return {void}
         */
        currentInlineEdit(id) {
            if (id !== this.$.uid && !this.form.busy) {
                this.edit = false;
            }
        },

        /**
         * Watches the edit value and emits the change.
         *
         * @param {String} val
         * @return {void}
         */
        editValue(val) {
            this.$emit('input', val);
        },

        /**
         * Watches the form for a successful save to clear the edit mode.
         *
         * @return {void}
         */
        'form.successful'(successful) {
            if (successful) {
                this.edit = false;
            }
        },
    },

    methods: {
        /**
         * Initializes the event handlers for the component.
         *
         * @return {void}
         */
        initEventHandlers() {
            document.body.addEventListener('keyup', this.handleEscapeKey);
        },

        /**
         * Clears the event handlers for the component.
         *
         * @return {void}
         */
        clearEventHandlers() {
            document.body.removeEventListener('keyup', this.handleEscapeKey);
        },

        /**
         * Handles the escape key event.
         *
         * @param {Object} e
         * @return {void}
         */
        handleEscapeKey(e) {
            if (e.keyCode === 27 && this.edit && !this.form.busy) {
                this.edit = false;
            }
        },
    },

    created() {
        this.editValue = this.value;
    },

    beforeUnmount() {
        this.clearEventHandlers();
    },
};
</script>
